import { createTableBulkActions } from "./createTableBulkActions";
import { createTableDocumentActions } from "./createTableDocumentActions";
import { createTableMenuActions } from "./createTableMenuActions";
import { createTablePagination } from "./createTablePagination";
import { createTablePreviewHandlers } from "./createTablePreviewHandlers";
import { createTableSelection } from "./createTableSelection";
import { createTableTeaching } from "./createTableTeaching";
import { documentTableOptions } from "./documentTableOptions";
import type { DocumentListTableProps } from "./documentTableTypes";
import { useTableBasics } from "./useTableBasics";
import { useTableClickActions } from "./useTableClickActions";
import { useTableComments } from "./useTableComments";
import { useTableCompare } from "./useTableCompare";
import { useTableContainerCounts } from "./useTableContainerCounts";
import { useTableDialogState } from "./useTableDialogState";
import { useTableDocumentKinds } from "./useTableDocumentKinds";
import { useTableDocumentMetadata } from "./useTableDocumentMetadata";
import { useTableMoveActions } from "./useTableMoveActions";
import { useTableMoveState } from "./useTableMoveState";
import { useTablePreviewState } from "./useTablePreviewState";
import { useTableRows } from "./useTableRows";
import { useTableSelectionState } from "./useTableSelectionState";
import { useTableSigning } from "./useTableSigning";
import { useTableSigningState } from "./useTableSigningState";
import { useTableSizing } from "./useTableSizing";
import { useTableTagActions } from "./useTableTagActions";
import { useTableVirtualRows } from "./useTableVirtualRows";

function useTableInitialState(props: DocumentListTableProps) {
  const options = documentTableOptions(props);
  const basics = useTableBasics(options);
  const moveState = useTableMoveState(options);
  const signingState = useTableSigningState(options);
  const comments = useTableComments(options);
  const selectionState = useTableSelectionState();
  const previewState = useTablePreviewState();
  const dialogState = useTableDialogState(options);
  const teaching = createTableTeaching({ ...dialogState, ...options, ...comments });
  const tagActions = useTableTagActions({ ...options, ...previewState, ...basics });
  return {
    ...options,
    ...basics,
    ...moveState,
    ...signingState,
    ...comments,
    ...selectionState,
    ...previewState,
    ...dialogState,
    ...teaching,
    ...tagActions,
  };
}

function useTablePresentationState(previous: ReturnType<typeof useTableInitialState>) {
  const signing = useTableSigning(previous);
  const documentKinds = useTableDocumentKinds({ ...previous, ...signing });
  const moveActions = useTableMoveActions({ ...previous, ...documentKinds });
  const rows = useTableRows({ ...previous, ...documentKinds });
  const containerCounts = useTableContainerCounts(documentKinds);
  const documentMetadata = useTableDocumentMetadata({ ...documentKinds, ...previous });
  const pagination = createTablePagination(previous);
  const sizing = useTableSizing({ ...rows, ...previous });
  const virtualRows = useTableVirtualRows({ ...rows, ...sizing, ...previous });
  return {
    ...previous,
    ...signing,
    ...documentKinds,
    ...moveActions,
    ...rows,
    ...containerCounts,
    ...documentMetadata,
    ...pagination,
    ...sizing,
    ...virtualRows,
  };
}

function useTableInteractionState(previous: ReturnType<typeof useTablePresentationState>) {
  const selection = createTableSelection(previous);
  const compare = useTableCompare(previous);
  const documentActions = createTableDocumentActions(previous);
  const previewHandlers = createTablePreviewHandlers(previous);
  const bulkActions = createTableBulkActions({ ...previous, ...compare });
  const menuActions = createTableMenuActions({
    ...previous,
    ...compare,
    ...bulkActions,
    ...documentActions,
  });
  const clickActions = useTableClickActions(previous);
  return {
    ...previous,
    ...selection,
    ...compare,
    ...documentActions,
    ...previewHandlers,
    ...bulkActions,
    ...menuActions,
    ...clickActions,
  };
}

export function useDocumentTableState(props: DocumentListTableProps) {
  const initial = useTableInitialState(props);
  const presentation = useTablePresentationState(initial);
  return useTableInteractionState(presentation);
}
export type DocumentTableState = ReturnType<typeof useDocumentTableState>;
